import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { Link } from "react-router-dom";
import {
  MdNotifications,
  MdSettings,
  MdArrowDropDown,
  MdLogout,
  MdMenu,
  MdChat,
} from "react-icons/md";
import NotificationServices from "../services/NotificationServices";
import ChatServices from "../services/ChatServices";
import "../styles/TopBar.css";
import moment from "moment";
import { io } from "socket.io-client";
import toast from "react-hot-toast";

const TopBar = ({
  title = "Dashboard",
  showMenuButton = false,
  onMenuClick,
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifTab, setNotifTab] = useState("ALL"); // ALL | UNREAD
  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);

  const { user, logout } = useContext(AuthContext);
  const history = useHistory();
  const notificationApi = new NotificationServices();
  const chatApi = useMemo(() => new ChatServices(), []);

  /** Support chat is staff-facing here; mobile users never see this bar. */
  const canSeeChat = [1, 2, 3].includes(Number(user?.role_id));

  const handleLogout = () => {
    logout();
    history.push("/login");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search functionality
    console.log("Searching for:", searchQuery);
  };

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await notificationApi.getUnreadCount();
      if (res?.isSuccess) {
        setUnreadCount(res.count || 0);
      }
    } catch (_err) {
      // silent
    }
  }, [notificationApi]);

  const loadNotifications = useCallback(
    async (tab = notifTab) => {
      if (!user) return;
      setNotifLoading(true);
      try {
        const status = tab === "UNREAD" ? "UNREAD" : "ALL";
        const res = await notificationApi.getNotificationList(
          1,
          20,
          "",
          status,
        );
        if (res?.isSuccess) {
          setNotifications(res.data || []);
        }
      } catch (_err) {
        // silent or toast if desired
      } finally {
        setNotifLoading(false);
      }
    },
    [notifTab, notificationApi, user],
  );

  const loadChatUnread = useCallback(async () => {
    if (!canSeeChat) return;
    try {
      const res = await chatApi.getUnreadCount();
      if (res?.isSuccess) setChatUnread(Number(res.data?.unread || 0));
    } catch (_err) {
      // Keep the last known figure rather than flashing the badge away.
    }
  }, [chatApi, canSeeChat]);

  // Auto-refresh badge
  useEffect(() => {
    if (!user) return;
    loadUnreadCount();
    loadChatUnread();
    const id = setInterval(() => {
      loadUnreadCount();
      loadChatUnread();
    }, 15000);
    return () => clearInterval(id);
  }, [user, loadUnreadCount, loadChatUnread]);

  /**
   * Live push for events that need to reach an approver immediately - the 15s
   * poll above is the fallback, not the primary path.
   */
  useEffect(() => {
    // The login payload names this user_id, not id.
    const currentUserId = user?.user_id;
    if (!currentUserId) return undefined;

    const socket = io(process.env.REACT_APP_NODE_URL, {
      transports: ["websocket", "polling"],
    });

    let cleanupChatRead = () => {};
    const register = () => socket.emit("registerUser", { userId: currentUserId });
    socket.on("connect", register);

    socket.on("newDepositRequest", (payload) => {
      toast.success(
        `New deposit request: Rs.${payload?.amount} from ${payload?.player?.name || "a player"}`,
        { duration: 6000 },
      );
      loadUnreadCount();

      // TopBar is mounted on every page and owns the only socket connection,
      // so it re-broadcasts the event locally. Screens that need to react
      // (e.g. the deposit queue refreshing itself) listen for this instead of
      // opening a second socket and raising a duplicate toast.
      window.dispatchEvent(
        new CustomEvent("yantra:newDepositRequest", { detail: payload }),
      );
    });

    socket.on("newWithdrawalRequest", (payload) => {
      toast.success(
        `New withdrawal request: Rs.${payload?.amount} from ${payload?.player?.name || "a player"}`,
        { duration: 6000 },
      );
      loadUnreadCount();
      window.dispatchEvent(
        new CustomEvent("yantra:newWithdrawalRequest", { detail: payload }),
      );
    });

    // Support chat. Only a player's own message is worth interrupting an
    // admin for - a reply from another staff member on the same thread is an
    // echo, and the open chat screen shows it without a toast.
    socket.on("chatMessage", (payload) => {
      if (Number(payload?.sender_role) === 4) {
        toast.success(
          `New support message from ${payload?.sender_name || "a player"}`,
          { duration: 5000 },
        );
        // Nudge the badge now; the 15s poll corrects it if the open chat page
        // marks that same thread read a moment later.
        setChatUnread((c) => c + 1);
      }
      window.dispatchEvent(
        new CustomEvent("yantra:chatMessage", { detail: payload }),
      );
    });

    // An attachment removed elsewhere must vanish here too - the open thread
    // would otherwise keep a thumbnail whose URL now 404s.
    socket.on("chatImageDeleted", (payload) => {
      window.dispatchEvent(
        new CustomEvent("yantra:chatImageDeleted", { detail: payload }),
      );
    });

    // The chat page announces when it has cleared a thread, so the badge does
    // not sit stale for up to 15s after an admin reads the messages.
    const onChatRead = () => loadChatUnread();
    window.addEventListener("yantra:chatRead", onChatRead);
    cleanupChatRead = () =>
      window.removeEventListener("yantra:chatRead", onChatRead);

    return () => {
      socket.off("connect", register);
      socket.off("newDepositRequest");
      socket.off("newWithdrawalRequest");
      socket.off("chatMessage");
      socket.off("chatImageDeleted");
      cleanupChatRead();
      socket.disconnect();
    };
  }, [user, loadUnreadCount, loadChatUnread]);

  const handleToggleNotifications = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    if (next) {
      loadNotifications("ALL");
      setNotifTab("ALL");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await notificationApi.markAllRead();
      if (res?.isSuccess) {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
        setUnreadCount(0);
      }
    } catch (_err) {
      // ignore
    }
  };

  const handleClickNotification = async (n) => {
    if (!n || n.is_read) return;
    try {
      await notificationApi.markRead(n.id);
      setNotifications((prev) =>
        prev.map((item) => (item.id === n.id ? { ...item, is_read: 1 } : item)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_err) {
      // ignore
    }
  };

  return (
    <header className="topbar">
      <div className="topbar-container">
        {/* Left Section - Title */}
        <div className="topbar-left">
          {showMenuButton && (
            <button
              type="button"
              className="topbar-menu-btn"
              aria-label="Open menu"
              onClick={() => onMenuClick && onMenuClick()}
            >
              <MdMenu />
            </button>
          )}
          <h1 className="topbar-title">{title}</h1>
        </div>

        {/* Center Section - Search */}
        {/* <div className="topbar-center">
          <form className="search-form" onSubmit={handleSearch}>
            <MdSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search for Menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </form>
        </div> */}

        {/* Right Section - Icons & User Menu */}
        <div className="topbar-right">
          {/* Support chat */}
          {canSeeChat && (
            <button
              className="topbar-icon-btn"
              title={
                chatUnread > 0
                  ? `Support chat - ${chatUnread} unread`
                  : "Support chat"
              }
              aria-label="Support chat"
              onClick={() => {
                // Clear straight away: the page marks everything read on open,
                // so leaving the badge up until the next poll reads as a bug.
                setChatUnread(0);
                history.push("/support-chat");
              }}
            >
              <MdChat />
              {chatUnread > 0 && (
                <span className="notification-badge">
                  {chatUnread > 9 ? "9+" : chatUnread}
                </span>
              )}
            </button>
          )}

          {/* Notifications */}
          <button
            className="topbar-icon-btn"
            title="Notifications"
            onClick={handleToggleNotifications}
          >
            <MdNotifications />
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* User Menu */}
          <div className="user-menu-wrapper">
            <button
              className="user-menu-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">{user?.name?.charAt(0) || "U"}</div>
              <MdArrowDropDown
                className={`dropdown-icon ${showUserMenu ? "open" : ""}`}
              />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-avatar">
                    {user?.name?.charAt(0) || "U"}
                  </div>
                  <div className="dropdown-user-info">
                    <p className="dropdown-name">{user?.name || "User"}</p>
                    <p className="dropdown-email">{user?.role || "user"}</p>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <div className="dropdown-menu-ll">
                  <Link to="/profile" className="dropdown-item">
                    👤 My Profile
                  </Link>
                  <Link to="/settings" className="dropdown-item">
                    ⚙️ Settings
                  </Link>
                </div>

                <div className="dropdown-divider" />

                <button className="dropdown-logout" onClick={handleLogout}>
                  <MdLogout />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Close dropdown when clicking outside */}
      {showUserMenu && (
        <div
          className="dropdown-overlay"
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* Notification panel */}
      {showNotifications && (
        <>
          <div className="notif-panel">
            <div className="notif-header">
              <div>
                <h3>Notifications</h3>
              </div>
              <button
                className="notif-close-btn"
                onClick={() => setShowNotifications(false)}
              >
                ×
              </button>
            </div>

            <div className="notif-tabs">
              <button
                className={`notif-tab ${notifTab === "ALL" ? "active" : ""}`}
                onClick={() => {
                  setNotifTab("ALL");
                  loadNotifications("ALL");
                }}
              >
                All
              </button>
              <button
                className={`notif-tab ${notifTab === "UNREAD" ? "active" : ""}`}
                onClick={() => {
                  setNotifTab("UNREAD");
                  loadNotifications("UNREAD");
                }}
              >
                Unread
              </button>
              <button
                className="notif-mark-all"
                onClick={handleMarkAllRead}
                disabled={!unreadCount}
              >
                Mark all as read
              </button>
            </div>

            <div className="notif-list">
              {notifLoading ? (
                <div className="notif-empty">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="notif-empty">No notifications</div>
              ) : (
                notifications.map((n) => {
                  const createdAt = n.created_at
                    ? new Date(n.created_at)
                    : null;
                  const initials = (n.title || "Nt")
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((s) => s.charAt(0).toUpperCase())
                    .join("");

                  const isReceiver = user && n.receiver_id === user.user_id;
                  const body = (isReceiver && n.receiver_body) || n.body || "";

                  return (
                    <div
                      key={n.id}
                      className={`notif-item ${n.is_read ? "" : "unread"}`}
                      onClick={() => handleClickNotification(n)}
                    >
                      <div className="notif-avatar">{initials}</div>
                      <div className="notif-content">
                        <div className="notif-title">
                          {n.title || "Notification"}
                        </div>
                        <div className="notif-body">{body}</div>
                        {createdAt && (
                          <div className="notif-time">
                            {moment.utc(createdAt).format('DD-MMM-YYYY hh:mm A')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div
            className="notif-overlay"
            onClick={() => setShowNotifications(false)}
          />
        </>
      )}
    </header>
  );
};

export default TopBar;
